import { Order, DeliveryInfo, DietaryRequirements } from '../../domain/entities/Order';
import { OrderItem, ProductAttribute } from '../../domain/entities/OrderItem';
import { OrderRepository } from '../../domain/repositories/OrderRepository';
import { ProductRepository } from '../../domain/repositories/ProductRepository';
import { Email } from '../../domain/value-objects/Email';
import { PhoneNumber } from '../../domain/value-objects/PhoneNumber';
import { Address } from '../../domain/value-objects/Address';
import { Money } from '../../domain/value-objects/Money';
import { ProductId } from '../../domain/value-objects/ProductId';
import { CreateOrderDto, OrderResponseDto } from '../dtos/OrderDto';

export class SubmitOrderUseCase {
  constructor(
    private readonly orderRepository: OrderRepository,
    private readonly productRepository: ProductRepository,
  ) {}

  async execute(createOrderDto: CreateOrderDto): Promise<OrderResponseDto> {
    // Validate input
    await this.validateOrder(createOrderDto);

    // Create domain objects
    const orderItems = await this.createOrderItems(createOrderDto.items);
    const deliveryInfo = this.createDeliveryInfo(createOrderDto.deliveryInfo);
    const dietaryRequirements = this.createDietaryRequirements(createOrderDto.dietaryRequirements);

    // Create order entity
    const order = Order.create(orderItems, deliveryInfo, dietaryRequirements);

    // Save order
    await this.orderRepository.save(order);

    // Return response DTO
    return this.mapToResponseDto(order);
  }

  private async validateOrder(dto: CreateOrderDto): Promise<void> {
    if (!dto.items || dto.items.length === 0) {
      throw new Error('Order must contain at least one item');
    }

    // Validate each item exists and is available
    for (const item of dto.items) {
      const variant = await this.productRepository.findVariantById(item.variantId);
      if (!variant) {
        throw new Error(`Product variant not found: ${item.variantId}`);
      }

      if (!variant.isAvailable()) {
        throw new Error(`Product variant is not available: ${item.variantId}`);
      }

      if (item.quantity <= 0) {
        throw new Error('Item quantity must be greater than zero');
      }
    }

    // Validate delivery info
    if (!Email.isValidEmailFormat(dto.deliveryInfo.email)) {
      throw new Error('Invalid email address');
    }

    if (!PhoneNumber.isValidUsPhoneNumber(dto.deliveryInfo.phoneNumber)) {
      throw new Error('Invalid phone number');
    }

    if (dto.deliveryInfo.streetAddress && !Address.isValidUsPostalCode(dto.deliveryInfo.zipCode)) {
      throw new Error('Invalid zip code');
    }
  }

  private async createOrderItems(itemDtos: CreateOrderDto['items']): Promise<OrderItem[]> {
    const orderItems: OrderItem[] = [];

    for (const itemDto of itemDtos) {
      const variant = await this.productRepository.findVariantById(itemDto.variantId);
      if (!variant) {
        throw new Error(`Product variant not found: ${itemDto.variantId}`);
      }

      const productId = ProductId.fromExistingId(itemDto.productId);
      const price = Money.fromAmount(itemDto.price);
      const attributes: ProductAttribute[] = itemDto.attributes.map((attr) => ({
        key: attr.key,
        value: attr.value,
        label: attr.label,
      }));

      const orderItem = OrderItem.create(
        itemDto.variantId,
        productId,
        itemDto.sku,
        itemDto.productName,
        price,
        itemDto.quantity,
        attributes,
        itemDto.note || '',
      );

      orderItems.push(orderItem);
    }

    return orderItems;
  }

  private createDeliveryInfo(dto: CreateOrderDto['deliveryInfo']): DeliveryInfo {
    const email = Email.fromEmailAddress(dto.email);
    const phoneNumber = PhoneNumber.fromPhoneNumber(dto.phoneNumber);

    let address: Address | null = null;
    if (dto.streetAddress) {
      address = Address.fromAddressComponents(dto.streetAddress, dto.city, dto.state, dto.zipCode);
    }

    return {
      fullName: dto.fullName,
      phoneNumber,
      email,
      contactMethod: dto.contactMethod,
      address,
      deliveryDate: new Date(dto.deliveryDate),
      deliveryTime: dto.deliveryTime,
    };
  }

  private createDietaryRequirements(dto: CreateOrderDto['dietaryRequirements']): DietaryRequirements {
    return {
      vegetarian: dto.vegetarian || false,
      vegan: dto.vegan || false,
      glutenFree: dto.glutenFree || false,
      dairyFree: dto.dairyFree || false,
      nuts: dto.nuts || false,
      shellfish: dto.shellfish || false,
      eggs: dto.eggs || false,
      soy: dto.soy || false,
      additionalComments: dto.additionalComments || '',
    };
  }

  private mapToResponseDto(order: Order): OrderResponseDto {
    const items = order.getItems().map((item) => ({
      variantId: item.getVariantId(),
      productId: item.getProductId().getUniqueIdentifier(),
      sku: item.getSku(),
      productName: item.getProductName(),
      price: item.getPrice().getAmountInDollars(),
      quantity: item.getQuantity(),
      attributes: item.getAttributes(),
      note: item.getNote(),
    }));

    const deliveryInfo = order.getDeliveryInfo();
    const dietaryRequirements = order.getDietaryRequirements();

    return {
      id: order.getId().getUniqueIdentifier(),
      status: order.getStatus(),
      total: order.calculateTotal().getAmountInDollars(),
      itemCount: order.getTotalItemCount(),
      createdAt: order.getCreatedAt().toISOString(),
      items,
      deliveryInfo: {
        fullName: deliveryInfo.fullName,
        phoneNumber: deliveryInfo.phoneNumber.getNormalizedDigits(),
        email: deliveryInfo.email.getEmailAddress(),
        contactMethod: deliveryInfo.contactMethod,
        streetAddress: deliveryInfo.address?.getStreetAddressLine() || null,
        city: deliveryInfo.address?.getCityName() || deliveryInfo.fullName, // Fallback for compatibility
        state: deliveryInfo.address?.getStateCode() || '',
        zipCode: deliveryInfo.address?.getPostalCode() || '',
        deliveryDate: deliveryInfo.deliveryDate.getTime(),
        deliveryTime: deliveryInfo.deliveryTime,
      },
      dietaryRequirements: {
        vegetarian: dietaryRequirements.vegetarian,
        vegan: dietaryRequirements.vegan,
        glutenFree: dietaryRequirements.glutenFree,
        dairyFree: dietaryRequirements.dairyFree,
        nuts: dietaryRequirements.nuts,
        shellfish: dietaryRequirements.shellfish,
        eggs: dietaryRequirements.eggs,
        soy: dietaryRequirements.soy,
        additionalComments: dietaryRequirements.additionalComments,
      },
      isPaid: order.isPaid(),
      paymentStatus: order.getPaymentStatus(),
    };
  }
}
